import { AuthenticatedVisualizationActionIframe } from "@app/components/assistant/conversation/actions/AuthenticatedVisualizationActionIframe";
import { useConversationSidePanelContext } from "@app/components/assistant/conversation/ConversationSidePanelContext";
import { ConversationSidePanelHeader } from "@app/components/assistant/conversation/ConversationSidePanelHeader";
import { CenteredState } from "@app/components/assistant/conversation/interactive_content/CenteredState";
import { ExportContentDropdown } from "@app/components/assistant/conversation/interactive_content/ExportContentDropdown";
import { ShareFrameSheet } from "@app/components/assistant/conversation/interactive_content/frame/ShareFrameSheet";
import { usePanelFullScreen } from "@app/components/assistant/conversation/usePanelFullScreen";
import { ConfirmContext } from "@app/components/Confirm";
import { PinPodBannerButton } from "@app/components/pod/files/PinPodBannerButton";
import { PodFrameTabButton } from "@app/components/pod/files/PodFrameTabButton";
import { useVisualizationRevert } from "@app/hooks/conversations";
import { useSendNotification } from "@app/hooks/useNotification";
import { useAuth, useFeatureFlags } from "@app/lib/auth/AuthContext";
import { useClientType } from "@app/lib/context/clientType";
import { clientFetch } from "@app/lib/egress/client";
import { useFileContent, useFileMetadata } from "@app/lib/swr/files";
import { usePodFiles } from "@app/lib/swr/pods";
import { useSpaceInfo } from "@app/lib/swr/spaces";
import { getErrorFromResponse } from "@app/lib/swr/swr";
import { useIsMobile } from "@app/lib/swr/useIsMobile";
import type { ConversationWithoutContentType } from "@app/types/assistant/conversation";
import { getFrameDisplayTitle } from "@app/types/files";
import { normalizeAsInternalDustError } from "@app/types/shared/utils/error_utils";
import type { LightWorkspaceType } from "@app/types/user";
import {
  Button,
  CheckCircle,
  Maximize01,
  Minimize01,
  RefreshCw01,
  ReverseLeft,
  Spinner,
  UploadCloud02,
} from "@dust-tt/sparkle";
import { useCallback, useContext, useMemo, useRef, useState } from "react";

interface FrameRendererProps {
  conversation?: ConversationWithoutContentType;
  fileId: string;
  projectId: string | null;
  owner: LightWorkspaceType;
  lastEditedByAgentConfigurationId?: string;
  contentHash?: string;
}

export function FrameRenderer({
  conversation,
  fileId,
  projectId,
  owner,
  lastEditedByAgentConfigurationId,
  contentHash,
}: FrameRendererProps) {
  const { vizUrl } = useAuth();
  const { hasFeature } = useFeatureFlags();
  const hasFrameTabs = hasFeature("pod_frame_tabs");
  const isMobile = useIsMobile();
  const [isLoading, setIsLoading] = useState(false);

  const { spaceInfo: projectInfo, isSpaceInfoLoading } = useSpaceInfo({
    workspaceId: owner.sId,
    spaceId: conversation?.spaceId ?? projectId ?? null,
  });

  const isFrameInPod = Boolean(
    projectId && projectInfo?.kind === "project" && !projectInfo.archivedAt
  );

  const projectSaveState = useMemo(() => {
    if (!projectInfo && isSpaceInfoLoading) {
      return "unknown";
    }
    if (
      !conversation?.spaceId ||
      !projectInfo ||
      projectInfo.kind !== "project"
    ) {
      return "unsupported";
    }
    if (!projectId) {
      return "supported";
    }

    return "saved";
  }, [conversation?.spaceId, projectId, projectInfo, isSpaceInfoLoading]);

  const { files: projectFiles } = usePodFiles({
    owner,
    podId: projectId ?? "",
    disabled: !isFrameInPod,
  });

  const framePath = useMemo(() => {
    const entry = projectFiles.find(
      (file) => !file.isDirectory && file.fileId === fileId
    );
    return entry?.path ?? null;
  }, [fileId, projectFiles]);

  const { closePanel, backToDetails } = useConversationSidePanelContext();
  const { isFullScreen, enterFullScreen, exitFullScreen, leaveFullScreen } =
    usePanelFullScreen();
  const clientType = useClientType();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // The space to resolve `project/` file paths inside the viz.
  // Priority: explicit project the frame was saved to -> the conversation's own project space
  // (a conversation can belong to a project space even before the frame is saved there).
  const frameSpaceId = projectId ?? conversation?.spaceId ?? null;

  const { fileContent, isFileContentLoading, error, mutateFileContent } =
    useFileContent({
      fileId,
      owner,
      cacheKey: contentHash,
    });

  const { fileMetadata, mutateFileMetadata } = useFileMetadata({
    fileId,
    owner,
  });

  const sendNotification = useSendNotification();
  const confirm = useContext(ConfirmContext);
  const [isSavingToProject, setIsSavingToProject] = useState(false);

  const { handleVisualizationRevert } = useVisualizationRevert({
    workspaceId: owner.sId,
    conversationId: conversation?.sId,
  });

  const hasPreviousVersion = (fileMetadata?.version ?? 0) > 1;

  const handleEditText = useCallback(
    async ({
      newText,
      oldText,
      targetFileId,
      source,
    }: {
      newText: string;
      oldText: string;
      targetFileId?: string;
      source?: string;
    }) => {
      try {
        // Location-based edits address the published entry Frame (the clicked bundle); the source
        // path inside `source` resolves within its build root. Legacy context edits route to the
        // nested target file.
        const editFileId = source ? fileId : (targetFileId ?? fileId);
        const response = await clientFetch(
          `/api/w/${owner.sId}/files/${editFileId}/edit-text`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ oldText, newText, source }),
          }
        );

        if (!response.ok) {
          const errorData = await getErrorFromResponse(response);
          return { success: false, error: errorData.message };
        }

        await mutateFileContent(
          `/api/w/${owner.sId}/files/${fileId}?action=view`
        );

        return { success: true };
      } catch (e) {
        return {
          success: false,
          error: normalizeAsInternalDustError(e).message,
        };
      }
    },
    [owner.sId, fileId, mutateFileContent]
  );

  const onClosePanel = () => {
    leaveFullScreen();
    closePanel();
  };

  const onBack = backToDetails
    ? () => {
        leaveFullScreen();
        backToDetails();
      }
    : undefined;

  const reloadFile = async () => {
    setIsLoading(true);
    await mutateFileContent(`/api/w/${owner.sId}/files/${fileId}?action=view`);
    setIsLoading(false);
  };

  const onRevert = () => {
    void handleVisualizationRevert({
      fileId,
      agentConfigurationId: lastEditedByAgentConfigurationId ?? "",
    });
  };

  const handleSaveToProject = useCallback(async () => {
    const projectIdToSave = conversation?.spaceId;
    if (!projectIdToSave) {
      return;
    }

    const confirmed = await confirm({
      title: (
        <>
          Save to <strong>{projectInfo?.name ?? "Project"}</strong>?
        </>
      ),
      message: (
        <>
          <div>
            The Frame will be part of the Project knowledge, and be able to be
            edited by any Project member.
          </div>
          <div>This action cannot be undone.</div>
        </>
      ),
      validateLabel: "Save",
      validateVariant: "primary",
    });
    if (!confirmed) {
      return;
    }
    setIsSavingToProject(true);
    try {
      const res = await clientFetch(
        `/api/w/${owner.sId}/files/${fileId}/save-in-project`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ projectId: projectIdToSave }),
        }
      );
      if (!res.ok) {
        const errorData = await getErrorFromResponse(res);
        sendNotification({
          type: "error",
          title: "Failed to save to Project",
          description: errorData.message,
        });
        return;
      }
      sendNotification({
        type: "success",
        title: "Saved to Project",
        description: `Frame saved to "${projectInfo?.name ?? "Project"}".`,
      });
      // Invalidate file metadata so parent and this component get updated projectId.
      await mutateFileMetadata();
    } catch (e) {
      sendNotification({
        type: "error",
        title: "Failed to save to Project",
        description: e instanceof Error ? e.message : "An error occurred",
      });
    } finally {
      setIsSavingToProject(false);
    }
  }, [
    confirm,
    conversation?.spaceId,
    fileId,
    mutateFileMetadata,
    owner.sId,
    projectInfo?.name,
    sendNotification,
  ]);

  if (error) {
    return (
      <div className="sheet flex h-panel flex-col">
        <ConversationSidePanelHeader onClose={onClosePanel} />
        <CenteredState>
          <p className="text-warning-500">
            Error loading file: {error.message}
          </p>
        </CenteredState>
      </div>
    );
  }

  return (
    <div className="sheet flex h-panel flex-col">
      <ConversationSidePanelHeader
        onClose={onClosePanel}
        onBack={onBack}
        backTooltip="Back to In this chat"
        title={
          fileMetadata ? getFrameDisplayTitle(fileMetadata.fileName) : undefined
        }
      >
        <div className="flex items-center">
          <div className="flex items-center">
            {conversation && (
              <>
                <Button
                  icon={RefreshCw01}
                  variant="ghost"
                  tooltip="Reload the file"
                  onClick={reloadFile}
                />
                {lastEditedByAgentConfigurationId && (
                  <Button
                    icon={ReverseLeft}
                    variant="ghost"
                    disabled={!hasPreviousVersion}
                    tooltip={
                      hasPreviousVersion
                        ? "Revert the last change"
                        : "No previous version"
                    }
                    onClick={onRevert}
                  />
                )}
                {clientType !== "extension" && (
                  <Button
                    icon={isFullScreen ? Minimize01 : Maximize01}
                    variant="ghost"
                    tooltip={`${isFullScreen ? "Exit" : "Go to"} full screen mode`}
                    onClick={isFullScreen ? exitFullScreen : enterFullScreen}
                  />
                )}
              </>
            )}
          </div>
          <div className="flex items-center">
            <ExportContentDropdown
              iframeRef={iframeRef}
              owner={owner}
              fileId={fileId}
              fileContent={fileContent ?? null}
              fileName={fileMetadata?.fileName}
            />
            <ShareFrameSheet
              key={contentHash ?? fileId}
              fileId={fileId}
              owner={owner}
              contentHash={contentHash}
            />
            <PinPodBannerButton
              owner={owner}
              spaceId={projectId ?? ""}
              pinnedFramePath={projectInfo?.pinnedFramePath ?? null}
              isEditor={projectInfo?.isEditor ?? false}
              framePath={framePath}
              fileName={fileMetadata?.fileName}
              hidden={!isFrameInPod}
            />
            {hasFrameTabs && (
              <PodFrameTabButton
                owner={owner}
                spaceId={projectId ?? ""}
                frameTabs={projectInfo?.frameTabs ?? []}
                tabsOrder={projectInfo?.tabsOrder ?? []}
                isEditor={projectInfo?.isEditor ?? false}
                framePath={framePath}
                fileName={fileMetadata?.fileName}
                hidden={!isFrameInPod}
              />
            )}
            {projectSaveState === "saved" && (
              <Button
                icon={CheckCircle}
                variant="ghost"
                disabled={true}
                label={isMobile ? undefined : "Saved"}
                tooltip={`Saved in "${projectInfo?.name ?? "unknown Project"}"`}
              />
            )}
            {projectSaveState === "supported" && (
              <Button
                icon={UploadCloud02}
                variant="ghost"
                label={
                  isMobile ? undefined : isSavingToProject ? "Saving…" : "Save"
                }
                isLoading={isSavingToProject}
                tooltip={`Save to "${projectInfo?.name ?? "unknown Project"}"`}
                onClick={handleSaveToProject}
              />
            )}
            <div className="mx-1 h-5 w-px bg-border" />
          </div>
        </div>
      </ConversationSidePanelHeader>

      <div className="flex-1 overflow-hidden">
        {isLoading || isFileContentLoading ? (
          <Spinner />
        ) : (
          <div className="h-full">
            <AuthenticatedVisualizationActionIframe
              agentConfigurationId={
                fileMetadata?.useCaseMetadata
                  .lastEditedByAgentConfigurationId ?? ""
              }
              workspaceId={owner.sId}
              vizUrl={vizUrl}
              visualization={{
                code: fileContent ?? "",
                complete: true,
                identifier: `viz-${fileId}`,
              }}
              key={`viz-${fileId}`}
              conversationId={conversation?.sId ?? null}
              isEditable={true}
              spaceId={frameSpaceId ?? undefined}
              isInDrawer={true}
              onEditText={handleEditText}
              ref={iframeRef}
            />
          </div>
        )}
      </div>
    </div>
  );
}
